import React, { useState, useEffect, useMemo } from 'react';
import {
  Info, TrendingDown, CalendarClock, Percent, DollarSign, Wallet,
  ArrowDown, ChevronUp, ChevronDown, Sparkles, Trophy, ArrowRight, GraduationCap,
} from 'lucide-react';
import {
  liabilityToDebt, simulate, compareStrategies, weightedAvgRate, strategyMeta,
  monthsToDate, formatMonthYear, formatCurrency,
} from '../lib/debtMath';

const STRATEGIES = ['avalanche', 'snowball', 'cashflow', 'custom'];
const EXTRA_PRESETS = [0, 100, 250, 500, 1000];

const ls = {
  get: (k, d) => {
    const v = localStorage.getItem(k);
    return v === null ? d : v;
  },
  set: (k, v) => localStorage.setItem(k, v),
};

const monthsLabel = (months) => {
  if (!isFinite(months)) return 'N/A';
  const y = Math.floor(months / 12);
  const m = months % 12;
  return `${y ? `${y} yr ` : ''}${m} mo`;
};

const Debt = ({ session, liabilities = [] }) => {
  const [autoImport, setAutoImport] = useState(ls.get('debt_autoImport', 'true') === 'true');
  const [strategy, setStrategy] = useState(ls.get('debt_strategy', 'avalanche'));
  const [extra, setExtra] = useState(parseFloat(ls.get('debt_extra', '0')) || 0);
  const [customOrder, setCustomOrder] = useState(() => {
    try { return JSON.parse(ls.get('debt_customOrder', '[]')); } catch { return []; }
  });

  useEffect(() => { ls.set('debt_autoImport', String(autoImport)); }, [autoImport]);
  useEffect(() => { ls.set('debt_strategy', strategy); }, [strategy]);
  useEffect(() => { ls.set('debt_extra', String(extra)); }, [extra]);
  useEffect(() => { ls.set('debt_customOrder', JSON.stringify(customOrder)); }, [customOrder]);

  // Debts come from liabilities (auto-import, like the Budget tab)
  const debts = useMemo(
    () => (autoImport ? liabilities.filter((l) => (parseFloat(l.value) || 0) > 0).map(liabilityToDebt) : []),
    [autoImport, liabilities]
  );

  // Keep custom order in sync with the live debt set
  const effectiveOrder = useMemo(() => {
    const ids = debts.map((d) => d.id);
    const kept = customOrder.filter((id) => ids.includes(id));
    const missing = ids.filter((id) => !kept.includes(id));
    return [...kept, ...missing];
  }, [customOrder, debts]);

  // Progress baseline (starting total) persisted so the meter shows reduction over time
  const totalDebt = debts.reduce((s, d) => s + d.balance, 0);
  useEffect(() => {
    const stored = parseFloat(ls.get('debt_baseline', '0')) || 0;
    if (totalDebt > stored) ls.set('debt_baseline', String(totalDebt));
  }, [totalDebt]);
  const baseline = Math.max(parseFloat(ls.get('debt_baseline', '0')) || 0, totalDebt);
  const progressPct = baseline > 0 ? Math.min(100, ((baseline - totalDebt) / baseline) * 100) : 0;

  const sim = useMemo(
    () => simulate(debts, { strategy, extra, customOrder: effectiveOrder }),
    [debts, strategy, extra, effectiveOrder]
  );
  const baseSim = useMemo(
    () => simulate(debts, { strategy, extra: 0, customOrder: effectiveOrder }),
    [debts, strategy, effectiveOrder]
  );
  const comparison = useMemo(() => compareStrategies(debts, extra), [debts, extra]);

  const totalMin = debts.reduce((s, d) => s + d.minPayment, 0);
  const avgRate = weightedAvgRate(debts);
  const missingRates = debts.filter((d) => d.rate === 0).length;
  const estimatedMins = debts.filter((d) => d.estimatedMin).length;

  const timeSavedMonths = isFinite(baseSim.months) && isFinite(sim.months) ? baseSim.months - sim.months : 0;
  const interestSaved = baseSim.totalInterest - sim.totalInterest;

  const insights = useMemo(() => buildInsights({ debts, sim, comparison }), [debts, sim, comparison]);

  const moveCustom = (id, dir) => {
    setCustomOrder(() => {
      const arr = [...effectiveOrder];
      const i = arr.indexOf(id);
      const j = dir === 'up' ? i - 1 : i + 1;
      if (i < 0 || j < 0 || j >= arr.length) return arr;
      [arr[i], arr[j]] = [arr[j], arr[i]];
      return arr;
    });
  };

  const orderedDebts = (strategy === 'custom' ? effectiveOrder : sim.order)
    .map((id) => debts.find((d) => d.id === id))
    .filter(Boolean);

  // Timeline grouped by year
  const timeline = useMemo(() => {
    const byYear = {};
    Object.values(sim.perDebt).forEach((p) => {
      if (p.date) {
        const y = p.date.getFullYear();
        (byYear[y] = byYear[y] || []).push(p);
      }
    });
    return Object.entries(byYear).sort((a, b) => a[0] - b[0]);
  }, [sim]);

  if (debts.length === 0) {
    return (
      <div className="space-y-6">
        <Disclaimer />
        <div className="card p-12 text-center">
          <TrendingDown className="h-10 w-10 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-700 font-medium">No debts to plan yet</p>
          <p className="text-gray-500 text-sm mt-1">
            {autoImport
              ? 'Add liabilities (with a balance) in the Net Worth tab — they\'ll appear here automatically.'
              : 'Auto-import is off. Turn it on to pull in your liabilities.'}
          </p>
          {!autoImport && (
            <button onClick={() => setAutoImport(true)} className="btn-primary btn-sm mx-auto mt-4">
              Turn on auto-import
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Disclaimer />

      {/* Summary cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <Summary label="Total Debt" value={formatCurrency(totalDebt)} Icon={Wallet} tint="danger" />
        <Summary label="Monthly Payments" value={`${formatCurrency(totalMin + extra)}`} Icon={DollarSign} tint="primary" />
        <Summary label="Avg Interest Rate" value={`${avgRate.toFixed(1)}%`} Icon={Percent} tint="warning" />
        <Summary label="Debt-Free Date" value={sim.solved ? formatMonthYear(sim.payoffDate) : 'N/A'} Icon={CalendarClock} tint="success" />
        <Summary label="Interest Remaining" value={formatCurrency(sim.totalInterest)} Icon={TrendingDown} tint="danger" />
      </div>

      {/* Auto-import toggle */}
      <div className="card p-4 flex items-start sm:items-center gap-3">
        <input
          id="debtAutoImport" type="checkbox" checked={autoImport}
          onChange={(e) => setAutoImport(e.target.checked)}
          className="mt-1 sm:mt-0 h-4 w-4 text-primary-600 rounded border-gray-300 focus:ring-primary-500"
        />
        <label htmlFor="debtAutoImport" className="text-sm text-gray-700 cursor-pointer">
          <span className="font-medium">Auto-import debts from liabilities</span> in the Net Worth tab
          <span className="text-gray-500"> — {debts.length} debt{debts.length !== 1 ? 's' : ''}, {formatCurrency(totalDebt)} total</span>
        </label>
      </div>

      {(missingRates > 0 || estimatedMins > 0) && (
        <div className="rounded-lg border border-warning-200 bg-warning-50 px-4 py-3 text-sm text-warning-800 flex items-start gap-2">
          <Info className="h-4 w-4 mt-0.5 shrink-0 text-warning-600" />
          <span>
            {missingRates > 0 && <>{missingRates} debt{missingRates !== 1 ? 's are' : ' is'} missing an interest rate. </>}
            {estimatedMins > 0 && <>{estimatedMins} {estimatedMins !== 1 ? 'debts use' : 'debt uses'} an estimated minimum payment. </>}
            Add interest rates &amp; monthly payments on each liability in the Net Worth tab for accurate strategies.
          </span>
        </div>
      )}

      {/* Progress meter */}
      <div className="card p-6">
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-semibold text-gray-900">Debt Reduction Progress</h3>
          <span className="text-sm font-semibold text-success-600">{Math.round(progressPct)}% paid off</span>
        </div>
        <div className="h-4 w-full rounded-full bg-gray-100 overflow-hidden">
          <div className="h-full bg-success-500 rounded-full transition-all" style={{ width: `${progressPct}%` }} />
        </div>
        <p className="text-xs text-gray-500 mt-2">
          Starting balance {formatCurrency(baseline)} · {formatCurrency(baseline - totalDebt)} eliminated · {formatCurrency(totalDebt)} remaining
        </p>
        <MilestoneRow pct={progressPct} eliminated={baseline - totalDebt} />
      </div>

      {/* Strategy selector */}
      <div className="card p-6">
        <h3 className="font-semibold text-gray-900 mb-4">Payoff Strategy</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {STRATEGIES.map((s) => (
            <button
              key={s}
              onClick={() => setStrategy(s)}
              className={`text-left rounded-lg border p-4 transition-colors ${
                strategy === s ? 'border-primary-500 bg-primary-50 ring-1 ring-primary-500' : 'border-gray-200 hover:bg-gray-50'
              }`}
            >
              <p className="font-medium text-gray-900">{strategyMeta[s].label}</p>
              <p className="text-xs text-gray-500 mt-1">{strategyMeta[s].blurb}</p>
            </button>
          ))}
        </div>

        {/* Payoff order */}
        <div className="mt-5">
          <p className="text-sm font-medium text-gray-600 mb-2">
            Payoff order {strategy === 'custom' && <span className="text-gray-400 font-normal">— reorder with the arrows</span>}
          </p>
          <div className="divide-y divide-gray-100 border border-gray-200 rounded-lg">
            {orderedDebts.map((d, i) => (
              <div key={d.id} className="flex items-center justify-between px-4 py-2.5">
                <div className="flex items-center gap-3 min-w-0">
                  <span className="h-6 w-6 rounded-full bg-primary-100 text-primary-700 text-xs font-semibold flex items-center justify-center shrink-0">{i + 1}</span>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{d.name}</p>
                    <p className="text-xs text-gray-500">
                      {formatCurrency(d.balance)} · {d.rate}% · min {formatCurrency(d.minPayment)}{d.estimatedMin ? ' (est)' : ''}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className="text-xs text-gray-400">{sim.perDebt[d.id]?.date ? formatMonthYear(sim.perDebt[d.id].date) : '—'}</span>
                  {strategy === 'custom' && (
                    <div className="flex flex-col">
                      <button onClick={() => moveCustom(d.id, 'up')} disabled={i === 0} className="text-gray-400 hover:text-gray-700 disabled:opacity-30"><ChevronUp className="h-4 w-4" /></button>
                      <button onClick={() => moveCustom(d.id, 'down')} disabled={i === orderedDebts.length - 1} className="text-gray-400 hover:text-gray-700 disabled:opacity-30"><ChevronDown className="h-4 w-4" /></button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Strategy comparison */}
      <div className="card p-6">
        <h3 className="font-semibold text-gray-900 mb-1">Strategy Comparison</h3>
        <p className="text-xs text-gray-500 mb-4">Same total monthly payment, different order. For educational purposes only.</p>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 border-b border-gray-200">
                <th className="py-2 pr-4 font-medium">Strategy</th>
                <th className="py-2 pr-4 font-medium">Payoff Date</th>
                <th className="py-2 pr-4 font-medium">Time</th>
                <th className="py-2 pr-4 font-medium">Interest Paid</th>
                <th className="py-2 font-medium">Interest Saved</th>
              </tr>
            </thead>
            <tbody>
              {comparison.results.map((r) => {
                const isRec = comparison.recommended === r.strategy;
                return (
                  <tr key={r.strategy} className={`border-b border-gray-100 ${isRec ? 'bg-success-50' : ''}`}>
                    <td className="py-2.5 pr-4 font-medium text-gray-900">
                      {r.label}
                      {isRec && <span className="badge-success ml-2">Recommended</span>}
                    </td>
                    <td className="py-2.5 pr-4 text-gray-700">{r.solved ? formatMonthYear(r.payoffDate) : 'N/A'}</td>
                    <td className="py-2.5 pr-4 text-gray-700">{monthsLabel(r.months)}</td>
                    <td className="py-2.5 pr-4 text-gray-700">{formatCurrency(r.totalInterest)}</td>
                    <td className="py-2.5 text-success-600 font-medium">{r.interestSaved > 0 ? formatCurrency(r.interestSaved) : '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <p className="text-xs text-gray-400 mt-3">
          “Recommended” = lowest projected interest. You can choose any strategy above.
        </p>
      </div>

      {/* Extra payment simulator + balance chart */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="card p-6">
          <h3 className="font-semibold text-gray-900 mb-1 flex items-center gap-2"><Sparkles className="h-4 w-4 text-primary-600" /> Extra Payment Simulator</h3>
          <p className="text-xs text-gray-500 mb-4">See how extra monthly payments speed up your {strategyMeta[strategy].label.toLowerCase()} plan.</p>
          <div className="flex flex-wrap gap-2 mb-4">
            {EXTRA_PRESETS.map((p) => (
              <button
                key={p}
                onClick={() => setExtra(p)}
                className={`px-3 py-1.5 rounded-md text-sm border ${extra === p ? 'bg-primary-600 text-white border-primary-600' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}
              >
                {p === 0 ? 'None' : `+${formatCurrency(p)}`}
              </button>
            ))}
          </div>
          <input
            type="range" min={0} max={2000} step={25} value={extra}
            onChange={(e) => setExtra(parseFloat(e.target.value))}
            className="w-full accent-primary-600"
          />
          <div className="text-center text-sm text-gray-600 mt-1">Extra: <span className="font-semibold text-gray-900">{formatCurrency(extra)}/mo</span></div>

          <div className="grid grid-cols-3 gap-3 mt-5 pt-4 border-t border-gray-100 text-center">
            <div>
              <p className="text-xs text-gray-500">Debt-Free</p>
              <p className="text-base font-bold text-gray-900">{sim.solved ? formatMonthYear(sim.payoffDate) : 'N/A'}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Time Saved</p>
              <p className="text-base font-bold text-success-600">{timeSavedMonths > 0 ? monthsLabel(timeSavedMonths) : '—'}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Interest Saved</p>
              <p className="text-base font-bold text-success-600">{interestSaved > 1 ? formatCurrency(interestSaved) : '—'}</p>
            </div>
          </div>
        </div>

        <div className="card p-6">
          <h3 className="font-semibold text-gray-900 mb-4">Projected Balance</h3>
          <BalanceChart schedule={sim.schedule} />
        </div>
      </div>

      {/* Waterfall + Timeline */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="card p-6">
          <h3 className="font-semibold text-gray-900 mb-1">Payment Waterfall</h3>
          <p className="text-xs text-gray-500 mb-4">As each debt is paid, its payment rolls into the next.</p>
          {sim.events.length === 0 ? (
            <p className="text-sm text-gray-500">No payoffs projected within range.</p>
          ) : (
            <div className="space-y-3">
              {sim.events.map((e, i) => (
                <div key={e.id} className="flex items-center gap-3">
                  <div className="h-8 w-8 rounded-full bg-success-100 flex items-center justify-center shrink-0">
                    <ArrowDown className="h-4 w-4 text-success-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{e.name} paid off</p>
                    <p className="text-xs text-gray-500">{formatMonthYear(e.date)}</p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-sm font-semibold text-success-600">+{formatCurrency(e.freed)}/mo</p>
                    <p className="text-xs text-gray-400">{formatCurrency(e.power)} rolling power</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="card p-6">
          <h3 className="font-semibold text-gray-900 mb-4">Payoff Timeline</h3>
          {timeline.length === 0 ? (
            <p className="text-sm text-gray-500">No payoffs projected within range.</p>
          ) : (
            <div className="relative pl-5 space-y-4">
              <div className="absolute left-[7px] top-1 bottom-1 w-px bg-gray-200" />
              {timeline.map(([year, items]) => (
                <div key={year} className="relative">
                  <div className="absolute -left-5 top-1 h-3.5 w-3.5 rounded-full bg-primary-500 border-2 border-white" />
                  <p className="text-sm font-semibold text-gray-900">{year}</p>
                  <ul className="mt-1 space-y-1">
                    {items.map((p, i) => (
                      <li key={i} className="text-sm text-gray-600 flex items-center gap-1.5">
                        <Trophy className="h-3.5 w-3.5 text-warning-500" /> {p.name} paid off
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
              {sim.solved && (
                <div className="relative">
                  <div className="absolute -left-5 top-1 h-3.5 w-3.5 rounded-full bg-success-500 border-2 border-white" />
                  <p className="text-sm font-semibold text-success-700">Debt Free · {formatMonthYear(sim.payoffDate)} 🎉</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Educational insights */}
      <div className="card p-6">
        <h3 className="font-semibold text-gray-900 mb-1 flex items-center gap-2"><GraduationCap className="h-4 w-4 text-primary-600" /> Educational Insights</h3>
        <p className="text-xs text-gray-500 mb-4">Automated observations for educational purposes only — not financial advice.</p>
        <ul className="space-y-2.5">
          {insights.map((t, i) => (
            <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
              <ArrowRight className="h-4 w-4 text-primary-500 mt-0.5 shrink-0" />
              <span>{t}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

// ---- helpers / subcomponents ----

const Disclaimer = () => (
  <div className="rounded-lg border border-primary-200 bg-primary-50 px-4 py-3 text-sm text-primary-800 flex items-start gap-2">
    <Info className="h-4 w-4 mt-0.5 shrink-0 text-primary-600" />
    <span><strong>For educational purposes only.</strong> These illustrations estimate payoff outcomes and are not individualized financial advice.</span>
  </div>
);

const tintMap = {
  primary: 'bg-primary-50 text-primary-600',
  success: 'bg-success-50 text-success-600',
  warning: 'bg-warning-50 text-warning-600',
  danger: 'bg-danger-50 text-danger-600',
};

const Summary = ({ label, value, Icon, tint }) => (
  <div className="card p-4 flex items-start justify-between">
    <div className="min-w-0">
      <p className="text-xs font-medium text-gray-600">{label}</p>
      <p className="text-lg font-bold text-gray-900 mt-1 truncate">{value}</p>
    </div>
    <div className={`h-9 w-9 rounded-lg flex items-center justify-center shrink-0 ${tintMap[tint]}`}>
      <Icon size={18} />
    </div>
  </div>
);

const MilestoneRow = ({ pct }) => {
  const marks = [25, 50, 75, 100];
  return (
    <div className="flex justify-between mt-4">
      {marks.map((m) => {
        const hit = pct >= m;
        return (
          <div key={m} className="flex flex-col items-center gap-1">
            <div className={`h-7 w-7 rounded-full flex items-center justify-center text-xs font-semibold ${hit ? 'bg-success-100 text-success-700' : 'bg-gray-100 text-gray-400'}`}>
              {hit ? '✓' : m}
            </div>
            <span className={`text-[10px] ${hit ? 'text-success-600' : 'text-gray-400'}`}>{m === 100 ? 'Debt-free' : `${m}%`}</span>
          </div>
        );
      })}
    </div>
  );
};

const BalanceChart = ({ schedule }) => {
  if (!schedule || schedule.length < 2) {
    return <p className="text-sm text-gray-500">Not enough data to chart.</p>;
  }
  // Sample down to ~60 points for a smooth line
  const step = Math.max(1, Math.floor(schedule.length / 60));
  const pts = schedule.filter((_, i) => i % step === 0 || i === schedule.length - 1);
  const maxBal = Math.max(...pts.map((p) => p.totalBalance), 1);
  const maxMonth = pts[pts.length - 1].month || 1;
  const W = 320, H = 160, padL = 8, padB = 16;
  const x = (m) => padL + (m / maxMonth) * (W - padL * 2);
  const y = (b) => (H - padB) - (b / maxBal) * (H - padB - 8);
  const path = pts.map((p, i) => `${i === 0 ? 'M' : 'L'} ${x(p.month).toFixed(1)},${y(p.totalBalance).toFixed(1)}`).join(' ');
  const area = `${path} L ${x(maxMonth).toFixed(1)},${H - padB} L ${x(0).toFixed(1)},${H - padB} Z`;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full">
      <defs>
        <linearGradient id="debtFill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.25" />
          <stop offset="100%" stopColor="#3b82f6" stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={area} fill="url(#debtFill)" />
      <path d={path} fill="none" stroke="#2563eb" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
      <text x={padL} y={12} fontSize="9" fill="#9ca3af">{formatCurrency(maxBal)}</text>
      <text x={W - padL} y={H - 4} fontSize="9" fill="#9ca3af" textAnchor="end">
        {isFinite(maxMonth) ? formatMonthYear(monthsToDate(maxMonth)) : ''}
      </text>
    </svg>
  );
};

function buildInsights({ debts, sim, comparison }) {
  const out = [];
  // Biggest interest contributor
  const contributors = Object.entries(sim.perDebt)
    .map(([id, p]) => ({ name: p.name, interest: p.interestPaid }))
    .sort((a, b) => b.interest - a.interest);
  if (contributors.length && sim.totalInterest > 0) {
    const top = contributors[0];
    const share = Math.round((top.interest / sim.totalInterest) * 100);
    if (share > 0) out.push(`${top.name} accounts for about ${share}% of your projected interest expense.`);
  }
  // Snowball vs avalanche
  const snow = comparison.results.find((r) => r.strategy === 'snowball');
  const aval = comparison.results.find((r) => r.strategy === 'avalanche');
  if (snow?.solved && aval?.solved) {
    const diff = snow.totalInterest - aval.totalInterest;
    if (diff > 50) out.push(`Switching from Snowball to Avalanche could save roughly ${formatCurrency(diff)} in interest.`);
    else out.push(`Snowball and Avalanche finish within ${formatCurrency(Math.abs(diff))} of each other here — pick the one that keeps you motivated.`);
  }
  // Extra payment nudge
  const plus = simulate(debts, { strategy: comparison.recommended || 'avalanche', extra: (sim.budget - sim.baseMin) + 150 });
  if (plus.solved && isFinite(sim.months)) {
    const saved = sim.months - plus.months;
    if (saved > 0) out.push(`Adding $150/month may shorten your payoff by about ${saved} month${saved !== 1 ? 's' : ''}.`);
  }
  if (sim.solved) out.push(`At your current plan you could be debt-free by ${formatMonthYear(sim.payoffDate)}.`);
  return out;
}

export default Debt;
